import { inject, Injectable } from '@angular/core';
import {
  WA_GEOLOCATION,
  WA_GEOLOCATION_SUPPORT,
} from '@ng-web-apis/geolocation';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class DynamicGeolocationService {
  private readonly geolocation = inject(WA_GEOLOCATION);
  private readonly geolocationSupport = inject(WA_GEOLOCATION_SUPPORT);

  public getCurrentGeolocationPosition$(
    options: PositionOptions,
  ): Observable<GeolocationPosition> {
    return new Observable<GeolocationPosition>(observer => {
      if (!this.geolocationSupport) {
        observer.error(new Error('Geolocation is not supported.'));

        return;
      }

      this.geolocation.getCurrentPosition(
        position => {
          observer.next(position);
          observer.complete();
        },
        error => observer.error(error),
        options,
      );
    });
  }

  public getAccuratePositionWithFallback$(options: {
    accurateTimeoutSeconds: number;
    fallbackTimeoutSeconds: number;
    maximumAgeMinutes?: number;
  }): Observable<GeolocationPosition> {
    const {
      accurateTimeoutSeconds,
      fallbackTimeoutSeconds,
      maximumAgeMinutes,
    } = options;

    const accuratePosition$ = this.getCurrentGeolocationPosition$({
      enableHighAccuracy: true,
      timeout: accurateTimeoutSeconds * 1000,
      maximumAge: maximumAgeMinutes ? maximumAgeMinutes * 60 * 1000 : undefined,
    });

    const fallbackPosition$ = this.getCurrentGeolocationPosition$({
      enableHighAccuracy: false,
      timeout: fallbackTimeoutSeconds * 1000,
      maximumAge: maximumAgeMinutes ? maximumAgeMinutes * 60 * 1000 : undefined,
    });

    return new Observable<GeolocationPosition>(observer => {
      const accurateSubscription = accuratePosition$.subscribe({
        next: position => {
          observer.next(position);
          observer.complete();
        },
        error: () => {
          const fallbackSubscription = fallbackPosition$.subscribe({
            next: fallbackPosition => {
              observer.next(fallbackPosition);
              observer.complete();
            },
            error: fallbackError => observer.error(fallbackError),
          });

          return () => fallbackSubscription.unsubscribe();
        },
      });

      return () => accurateSubscription.unsubscribe();
    });
  }
}
